import { EffectCarousel } from "ll-common-carousel";
import { fetchCart } from "ll-common-cart";
import { MenuDisclosure } from "ll-common-navigation";
import { DialogElement, Drawer, PopIn } from "ll-common-overlay";
import { extractSectionId, imageLoaded, matchesMediaQuery, waitForEvent } from "ll-common-utilities";
import { Delegate, ScrollOffset, animate, inView, scroll, stagger, timeline } from "vendor";
/* ─── announcement-bar ──────────────────────────────────────────────── */
var AnnouncementBarCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return {
      leaveControls: () => animate(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, { duration: 0.25, easing: [0.55, 0.055, 0.675, 0.19] }),
      enterControls: () => animate(toSlide, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0px)"] }, { duration: 0.4, easing: [0.215, 0.61, 0.355, 1] })
    };
  }
};
if (!window.customElements.get("announcement-bar-carousel")) {
  window.customElements.define("announcement-bar-carousel", AnnouncementBarCarousel);
}
/* ─── blog-posts ────────────────────────────────────────────────────── */
var BlogPosts = class extends HTMLElement {
  constructor() {
    super();
    if (this.hasAttribute("reveal-on-scroll") && matchesMediaQuery("motion-safe")) {
      inView(this, this.#reveal.bind(this), { margin: "-50px 0px" });
    }
  }
  #reveal() {
    this.style.opacity = "1";
    animate(
      this.children,
      { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] },
      { duration: 0.25, delay: stagger(0.1, { easing: "ease-out" }), easing: "ease" }
    );
  }
};
if (!window.customElements.get("blog-posts")) {
  window.customElements.define("blog-posts", BlogPosts);
}
/* ─── cart-drawer ───────────────────────────────────────────────────── */
var CartDrawer = class extends Drawer {
  #sectionId;
  constructor() {
    super(...arguments);
  }
  connectedCallback() {
    super.connectedCallback();
    this.#sectionId ?? (this.#sectionId = extractSectionId(this));
    document.addEventListener("cart:prepare-bundled-sections", this.#onBundleSection.bind(this), { signal: this.abortController.signal });
    document.addEventListener("cart:change", this.#onCartChange.bind(this), { signal: this.abortController.signal });
    document.addEventListener("cart:refresh", this.#refreshCart.bind(this), { signal: this.abortController.signal });
    window.addEventListener("pageshow", this.#onPageShow.bind(this), { signal: this.abortController.signal });
    this.addEventListener("dialog:before-show", this.#onBeforeShow);
  }
  #onBundleSection(event) {
    event.detail.sections.push(this.#sectionId);
  }
  async #onCartChange(event) {
    this.#replaceContent(event.detail.cart["sections"][this.#sectionId]);
    if ((window.themeVariables.settings.cartType === "drawer" || event.detail["onSuccessDo"] === "force_open_drawer") && event.detail.baseEvent === "variant:add") {
      this.show();
    }
  }
  async #onBeforeShow() {
    const drawerFooter = this.shadowRoot.querySelector('[part="footer"]');
    if (!drawerFooter) {
      return;
    }
    drawerFooter.style.opacity = "0";
    await waitForEvent(this, "dialog:after-show");
    animate(drawerFooter, { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] }, { duration: 0.25, easing: [0.25, 0.46, 0.45, 0.94] });
  }
  async #onPageShow(event) {
    if (!event.persisted) {
      return;
    }
    this.#refreshCart();
  }
  async #refreshCart() {
    this.#replaceContent(await (await fetch(`${Shopify.routes.root}?section_id=${this.#sectionId}`)).text());
  }
  async #replaceContent(html) {
    const domElement = new DOMParser().parseFromString(html, "text/html"), newCartDrawer = document.createRange().createContextualFragment(domElement.getElementById(`shopify-section-${this.#sectionId}`).querySelector("cart-drawer").innerHTML), itemCount = (await fetchCart)["item_count"];
    if (itemCount === 0) {
      const controls = timeline([
        [this.getShadowPartByName("body"), { opacity: [1, 0] }, { duration: 0.15, easing: "ease-in" }],
        [this.getShadowPartByName("footer"), { opacity: [1, 0], transform: ["translateY(0)", "translateY(30px)"] }, { duration: 0.15, at: "<", easing: "ease-in" }]
      ]);
      await controls.finished;
      this.replaceChildren(...newCartDrawer.children);
      animate(this.getShadowPartByName("body"), { opacity: [0, 1], transform: ["translateY(30px)", "translateY(0)"] }, { duration: 0.25, easing: [0.25, 0.46, 0.45, 0.94] });
    } else {
      this.replaceChildren(...newCartDrawer.children);
    }
    this.classList.toggle("ll-drawer--center-body", itemCount === 0);
    this.dispatchEvent(new CustomEvent("cart-drawer:refreshed", { bubbles: true }));
  }
};
if (!window.customElements.get("cart-drawer")) {
  window.customElements.define("cart-drawer", CartDrawer);
}
/* ─── customer ──────────────────────────────────────────────────────── */
var AccountLogin = class extends HTMLElement {
  constructor() {
    super();
    window.addEventListener("hashchange", this.#switchForm.bind(this));
    if (window.location.hash === "#recover") {
      this.#loginForm.hidden = true;
      this.#recoverForm.hidden = false;
    }
  }
  get #loginForm() {
    return this.querySelector("#login");
  }
  get #recoverForm() {
    return this.querySelector("#recover");
  }
  async #switchForm() {
    const fromForm = window.location.hash === "#recover" ? this.#loginForm : this.#recoverForm, toForm = window.location.hash === "#recover" ? this.#recoverForm : this.#loginForm;
    await animate(fromForm, { transform: ["translateY(0)", "translateY(30px)"], opacity: [1, 0] }, { duration: 0.6, easing: "ease" }).finished;
    fromForm.hidden = true;
    toForm.hidden = false;
    await animate(toForm, { transform: ["translateY(30px)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.6, easing: "ease" });
  }
};
if (!window.customElements.get("account-login")) {
  window.customElements.define("account-login", AccountLogin);
}
/* ─── faq ───────────────────────────────────────────────────────────── */
var FaqToc = class extends HTMLElement {
  #observer;
  constructor() {
    super(...arguments);
    this.#observer = new IntersectionObserver(this.#onObserve.bind(this), { rootMargin: "0px 0px -70% 0px" });
  }
  connectedCallback() {
    this.anchoredElements.forEach((anchoredElement) => this.#observer.observe(anchoredElement));
  }
  disconnectedCallback() {
    this.#observer.disconnect();
  }
  get anchorLinks() {
    return Array.from(this.querySelectorAll('a[href^="#"]'));
  }
  get anchoredElements() {
    return this.anchorLinks.map((anchor) => document.querySelector(anchor.getAttribute("href")));
  }
  #onObserve(entries) {
    for (const entry of entries) {
      const anchorLink = this.anchorLinks.find((anchor) => anchor.getAttribute("href") === `#${entry.target.id}`);
      if (!entry.isIntersecting && anchorLink.classList.contains("ll-is-active")) {
        continue;
      }
      if (entry.isIntersecting) {
        this.anchorLinks.forEach((link) => link.classList.toggle("ll-is-active", link === anchorLink));
      }
    }
  }
};
if (!window.customElements.get("faq-toc")) {
  window.customElements.define("faq-toc", FaqToc);
}
/* ─── featured-collections ──────────────────────────────────────────── */
var FeaturedCollectionsCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) {
    return {
      leaveControls: () => animate(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(15px)"] }, { duration: 0.3, easing: "ease-in" }),
      enterControls: () => animate(toSlide, { opacity: [0, 1], transform: ["translateY(15px)", "translateY(0)"] }, { duration: 0.2, delay: 0.2, easing: "ease-out" })
    };
  }
};
if (!window.customElements.get("featured-collections-carousel")) {
  window.customElements.define("featured-collections-carousel", FeaturedCollectionsCarousel);
}
/* ─── header ────────────────────────────────────────────────────────── */
var Header = class extends HTMLElement {
  #abortController;
  #headerTrackerIntersectionObserver;
  #isVisible;
  #scrollYTrackingPosition;
  constructor() {
    super(...arguments);
    this.#headerTrackerIntersectionObserver = new IntersectionObserver(this.#onHeaderTrackerIntersection.bind(this));
    this.#scrollYTrackingPosition = 0;
    this.#isVisible = true;
  }
  connectedCallback() {
    this.#abortController = new AbortController();
    const headerTracker = document.getElementById("header-scroll-tracker");
    if (!headerTracker) return;
    this.#headerTrackerIntersectionObserver.observe(headerTracker);
    if (this.hasAttribute("hide-on-scroll")) {
      window.addEventListener("scroll", this.#detectScrollDirection.bind(this), { signal: this.#abortController.signal });
      window.addEventListener("pointermove", this.#detectMousePosition.bind(this), { signal: this.#abortController.signal });
    }
  }
  disconnectedCallback() {
    this.#abortController.abort();
  }
  #onHeaderTrackerIntersection(entries) {
    this.classList.toggle("ll-is-solid", !entries[0].isIntersecting);
  }
  #detectMousePosition(event) {
    if (event.clientY < 100 && window.matchMedia("screen and (pointer: fine)").matches) {
      this.#setVisibility(true);
      this.#scrollYTrackingPosition = 0;
    }
  }
  #detectScrollDirection() {
    let isVisible;
    const scrollY = Math.max(0, window.scrollY);
    if (scrollY > this.#scrollYTrackingPosition && scrollY - this.#scrollYTrackingPosition > 100) {
      isVisible = false;
      this.#scrollYTrackingPosition = scrollY;
    } else if (window.scrollY < this.#scrollYTrackingPosition) {
      this.#scrollYTrackingPosition = scrollY;
      isVisible = true;
    }
    if (isVisible !== void 0) {
      this.#setVisibility(isVisible);
    }
  }
  #setVisibility(isVisible) {
    if (isVisible !== this.#isVisible) {
      if (!isVisible && this.querySelectorAll(":where(.ll-header__primary-nav, .ll-header__secondary-nav) [open]").length > 0) {
        return;
      }
      this.#isVisible = isVisible;
      document.documentElement.style.setProperty("--header-is-visible", isVisible ? "1" : "0");
      this.classList.toggle("ll-is-hidden", !isVisible);
    }
  }
};
var DropdownMenuDisclosure = class extends MenuDisclosure {
  createShowAnimationControls() {
    let menuItemsSequence = [];
    if (window.themeVariables.settings.staggerMenuApparition) {
      menuItemsSequence = [this.contentElement.querySelectorAll(":scope > li"), { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] }, { duration: 0.15, at: "-0.15", delay: stagger(0.1) }];
    }
    return timeline([
      [this.contentElement, { opacity: [0, 1] }, { duration: 0.25 }],
      menuItemsSequence
    ]);
  }
  createHideAnimationControls() {
    return timeline([
      [this.contentElement, { opacity: [1, 0] }, { duration: 0.4 }]
    ]);
  }
};
var MegaMenuDisclosure = class extends MenuDisclosure {
  createShowAnimationControls() {
    const linklists = Array.from(this.contentElement.querySelectorAll(".mega-menu__linklist > li"));
    let menuItemsSequence = [];
    if (window.themeVariables.settings.staggerMenuApparition) {
      menuItemsSequence = [
        { name: "content", at: "-0.5" },
        [linklists, { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] }, { duration: 0.3, at: "content", delay: stagger(0.1) }],
        [this.contentElement.querySelector(".mega-menu__promo"), { opacity: [0, 1] }, { duration: 0.3, at: "-0.15" }]
      ];
    }
    return timeline([
      [this.contentElement, { opacity: [0, 1] }, { duration: 0.25 }],
      ...menuItemsSequence
    ]);
  }
  createHideAnimationControls() {
    return timeline([
      [this.contentElement, { opacity: [1, 0] }, { duration: 0.4 }]
    ]);
  }
};
var HeaderSidebar = class extends Drawer {
  #buttonElements;
  #collapsiblePanel;
  constructor() {
    super();
    this.addEventListener("dialog:before-show", this.#onSidebarBeforeShow);
    this.addEventListener("dialog:after-show", this.#onSidebarAfterShow);
    this.addEventListener("dialog:before-hide", this.#onSidebarBeforeHide);
    this.addEventListener("dialog:after-hide", this.#onSidebarAfterHide);
  }
  connectedCallback() {
    super.connectedCallback();
    this.#collapsiblePanel = this.querySelector('[slot="collapsible-panel"]');
    this.#buttonElements = Array.from(this.querySelectorAll(".ll-header-sidebar__main-panel .ll-header-sidebar__linklist [aria-controls]"));
    this.#buttonElements.forEach((button) => button.addEventListener("click", this.#openCollapsiblePanel.bind(this), { signal: this.abortController.signal }));
  }
  revealItems(withDelay = false) {
    return timeline([
      [this.querySelector(".ll-header-sidebar__main-panel"), { opacity: 1, transform: "translateX(0)" }, { duration: 0, delay: withDelay ? 0.5 : 0 }],
      [this.querySelectorAll(".ll-header-sidebar__main-panel .ll-header-sidebar__linklist li"), { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] }, { duration: 0.15, at: "-0.15", delay: window.themeVariables.settings.staggerMenuApparition ? stagger(0.1) : 0 }],
      [this.querySelector(".ll-header-sidebar__footer"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 }]
    ]);
  }
  #openCollapsiblePanel(event) {
    this.#buttonElements.forEach((button) => button.setAttribute("aria-expanded", button === event.currentTarget ? "true" : "false"));
    this.#collapsiblePanel?.setAttribute("aria-activedescendant", event.currentTarget.getAttribute("aria-controls"));
    if (matchesMediaQuery("md-max")) {
      animate(this.querySelector(".ll-header-sidebar__main-panel"), { opacity: [1, 0], transform: ["translateX(0)", "translateX(-10px)"] }, { duration: 0.25 });
    }
  }
  #onSidebarBeforeShow() {
    animate(this.querySelector(".ll-header-sidebar__main-panel"), { opacity: 0, transform: "translateX(0)" }, { duration: 0 });
  }
  #onSidebarAfterShow() {
    this.revealItems();
  }
  #onSidebarBeforeHide() {
    if (matchesMediaQuery("md")) {
      this.#collapsiblePanel?.removeAttribute("aria-activedescendant");
      this.#buttonElements.forEach((button) => button.setAttribute("aria-expanded", "false"));
    }
  }
  #onSidebarAfterHide() {
    if (matchesMediaQuery("md-max")) {
      this.#collapsiblePanel?.removeAttribute("aria-activedescendant");
      this.#buttonElements.forEach((button) => button.setAttribute("aria-expanded", "false"));
    }
    Array.from(this.querySelectorAll("details")).forEach((detail) => detail.open = false);
  }
};
var HeaderSidebarCollapsiblePanel = class extends DialogElement {
  #sidebarDelegate;
  constructor() {
    super();
    this.#sidebarDelegate = new Delegate(this);
    this.#sidebarDelegate.on("click", '[data-action="close-panel"]', this.#closePanel.bind(this));
  }
  static get observedAttributes() {
    return [...super.observedAttributes, "aria-activedescendant"];
  }
  hideForOutsideClickTarget(target) {
    return false;
  }
  allowOutsideClickForTarget(target) {
    return target.closest(".ll-header-sidebar") !== void 0;
  }
  createEnterAnimationControls() {
    if (matchesMediaQuery("md-max")) {
      return timeline([
        [this, { opacity: [0, 1], transform: "translateX(0)" }, { duration: 0.3 }]
      ]);
    } else {
      return timeline([
        [this, { opacity: [0, 1], transform: ["translateX(0)", "translateX(calc(var(--transform-logical-flip) * 100%)"] }, { duration: 0.3 }]
      ]);
    }
  }
  createLeaveAnimationControls() {
    if (matchesMediaQuery("md-max")) {
      return timeline([
        [this, { opacity: [1, 0], transform: ["translateX(0)", "translateX(10px)"] }, { duration: 0.3 }]
      ]);
    } else {
      return timeline([
        [this, { opacity: [1, 0], transform: ["translateX(calc(var(--transform-logical-flip) * 100%))", "translateX(0)"] }, { duration: 0.3 }]
      ]);
    }
  }
  async attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === "aria-activedescendant") {
      if (oldValue === newValue) {
        return;
      }
      if (newValue !== null) {
        this.#switchPanel(this.querySelector(`#${oldValue}`), this.querySelector(`#${newValue}`));
      } else {
        await this.hide();
        Array.from(this.querySelectorAll(".ll-header-sidebar__sub-panel")).forEach((subPanel) => subPanel.hidden = true);
      }
    }
  }
  #closePanel() {
    this.removeAttribute("aria-activedescendant");
    this.closest("header-sidebar").revealItems(true);
  }
  async #switchPanel(fromPanel, toPanel) {
    if (!this.open) {
      await this.show();
    }
    if (fromPanel) {
      await animate(fromPanel, { opacity: [1, 0] }, { duration: 0.15 }).finished;
      fromPanel.hidden = true;
    }
    toPanel.hidden = false;
    const listSelector = matchesMediaQuery("md-max") ? ".header-sidebar__back-button, .header-sidebar__linklist li" : ".header-sidebar__linklist li";
    timeline([
      [toPanel, { opacity: 1 }, { duration: 0 }],
      [toPanel.querySelectorAll(listSelector), { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0)"] }, { duration: 0.15, at: "-0.15", delay: window.themeVariables.settings.staggerMenuApparition ? stagger(0.1) : 0 }],
      [toPanel.querySelector(".ll-header-sidebar__promo"), { opacity: [0, 1] }, { duration: 0.45 }]
    ]);
  }
};
if (!window.customElements.get("x-header")) {
  window.customElements.define("x-header", Header);
}
if (!window.customElements.get("dropdown-menu-disclosure")) {
  window.customElements.define("dropdown-menu-disclosure", DropdownMenuDisclosure);
}
if (!window.customElements.get("mega-menu-disclosure")) {
  window.customElements.define("mega-menu-disclosure", MegaMenuDisclosure);
}
if (!window.customElements.get("header-sidebar")) {
  window.customElements.define("header-sidebar", HeaderSidebar);
}
if (!window.customElements.get("header-sidebar-collapsible-panel")) {
  window.customElements.define("header-sidebar-collapsible-panel", HeaderSidebarCollapsiblePanel);
}
/* ─── image-with-text ───────────────────────────────────────────────── */
var ImageWithText = class extends HTMLElement {
  constructor() {
    super(...arguments);
  }
  connectedCallback() {
    if (matchesMediaQuery("motion-safe")) {
      inView(this.querySelector('[reveal-on-scroll="true"]'), ({ target }) => this.#onBecameVisible(target), { margin: "-200px 0px 0px 0px" });
    }
  }
  async #onBecameVisible(target) {
    await imageLoaded(target);
    const fromValue = (window.direction === "rtl" ? -1 : 1) * (matchesMediaQuery("md-max") ? 0.6 : 1) * (this.classList.contains("ll-image-with-text--reverse") ? 25 : -25);
    animate(
      target,
      { opacity: 1, transform: [`translateX(${fromValue}px)`, "translateX(0)"] },
      { easing: [0.215, 0.61, 0.355, 1] },
      { duration: 0.8 }
    );
  }
};
if (!window.customElements.get("image-with-text")) {
  window.customElements.define("image-with-text", ImageWithText);
}
/* ─── image-with-text-overlay ───────────────────────────────────────── */
var ImageWithTextOverlay = class extends HTMLElement {
  #preventInitialTransition;
  constructor() {
    super();
    this.#preventInitialTransition = false;
    if (Shopify.designMode) {
      this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => (this.#preventInitialTransition = event.detail.load));
    }
  }
  connectedCallback() {
    if (matchesMediaQuery("motion-safe") && this.getAttribute("reveal-on-scroll") === "true") {
      inView(this, ({ target }) => this.#onBecameVisible(target), { amount: 0.05 });
    }
  }
  async #onBecameVisible(target) {
    const media = target.querySelector(".ll-content-over-media > picture img, .ll-content-over-media > svg"), content = target.querySelector(".ll-content-over-media > :not(picture, svg)");
    await imageLoaded(media);
    const animationControls = timeline([
      [target, { opacity: 1 }],
      [media, { opacity: [0, 1], scale: [1.1, 1], easing: [0.215, 0.61, 0.355, 1], duration: 0.8 }],
      [content, { opacity: [0, 1], duration: 0.8 }]
    ]);
    if (this.#preventInitialTransition) {
      animationControls.finish();
    }
  }
};
if (!window.customElements.get("image-with-text-overlay")) {
  window.customElements.define("image-with-text-overlay", ImageWithTextOverlay);
}
/* ─── main-article ──────────────────────────────────────────────────── */
var ArticleToolbar = class extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      scroll((info) => {
        this.classList.toggle("ll-is-visible", info.y.progress > 0 && info.y.progress < 1);
      }, {
        target: this.closest(".shopify-section"),
        offset: ["100px start", "end start"]
      });
    }
  }
};
if (!window.customElements.get("article-toolbar")) {
  window.customElements.define("article-toolbar", ArticleToolbar);
}
/* ─── newsletter-popup ──────────────────────────────────────────────── */
var NewsletterPopup = class extends PopIn {
  connectedCallback() {
    super.connectedCallback();
    if (this.shouldAppearAutomatically) {
      setTimeout(() => this.show(), this.apparitionDelay);
    }
  }
  get apparitionDelay() {
    return parseInt(this.getAttribute("apparition-delay") || 0) * 1e3;
  }
  get shouldAppearAutomatically() {
    return !(localStorage.getItem("theme:popup-filled") === "true" || this.hasAttribute("only-once") && localStorage.getItem("theme:popup-appeared") === "true");
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === "open" && this.open) {
      localStorage.setItem("theme:popup-appeared", "true");
    }
  }
};
if (!window.customElements.get("newsletter-popup")) {
  window.customElements.define("newsletter-popup", NewsletterPopup);
}
/* ─── product ───────────────────────────────────────────────────────── */
var ProductStickyBar = class extends HTMLElement {
  #footerElement;
  #formElement;
  #intersectionObserver;
  #latestFooterCondition;
  #latestFormCondition;
  constructor() {
    super(...arguments);
    this.#intersectionObserver = new IntersectionObserver(this.#onFormVisibilityChange.bind(this));
    this.#latestFooterCondition = false;
    this.#latestFormCondition = false;
  }
  connectedCallback() {
    this.#formElement = document.forms[this.getAttribute("form")];
    this.#footerElement = document.querySelector(".shopify-section--footer");
    if (this.#formElement) {
      this.#intersectionObserver.observe(this.#formElement);
      this.#intersectionObserver.observe(this.#footerElement);
    }
  }
  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
  }
  #onFormVisibilityChange(entries) {
    const [formEntry, footerEntry] = [entries.find((entry) => entry.target === this.#formElement), entries.find((entry) => entry.target === this.#footerElement)];
    if (formEntry) {
      this.#latestFormCondition = !formEntry.isIntersecting && formEntry.boundingClientRect.bottom < 0;
    }
    if (footerEntry) {
      this.#latestFooterCondition = !footerEntry.isIntersecting;
    }
    this.classList.toggle("ll-is-visible", this.#latestFooterCondition && this.#latestFormCondition);
  }
};
if (!window.customElements.get("product-sticky-bar")) {
  window.customElements.define("product-sticky-bar", ProductStickyBar);
}
/* ─── product-recommendations ───────────────────────────────────────── */
var ProductRecommendations = class extends HTMLElement {
  #isLoaded;
  constructor() {
    super(...arguments);
    this.#isLoaded = false;
  }
  connectedCallback() {
    this.#loadRecommendations();
  }
  async #loadRecommendations() {
    if (this.#isLoaded) {
      return;
    }
    this.#isLoaded = true;
    const section = this.closest(".shopify-section"), intent = this.getAttribute("intent") || "related", url = `${Shopify.routes.root}recommendations/products?product_id=${this.getAttribute("product")}&limit=${this.getAttribute("limit") || 4}&section_id=${extractSectionId(section)}&intent=${intent}`, response = await fetch(url, { priority: intent === "related" ? "low" : "auto" });
    const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), productRecommendationsElement = tempDiv.querySelector("product-recommendations");
    if (productRecommendationsElement.childElementCount > 0) {
      this.replaceChildren(...document.importNode(productRecommendationsElement, true).childNodes);
      this.hidden = false;
    } else {
      this.remove();
    }
  }
};
if (!window.customElements.get("product-recommendations")) {
  window.customElements.define("product-recommendations", ProductRecommendations);
}
/* ─── recently-viewed-products ──────────────────────────────────────── */
var RecentlyViewedProducts = class extends HTMLElement {
  #isLoaded;
  constructor() {
    super(...arguments);
    this.#isLoaded = false;
  }
  connectedCallback() {
    this.#loadProducts();
  }
  get #searchQueryString() {
    const items = new Set(JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]"));
    if (this.hasAttribute("exclude-id")) {
      items.delete(parseInt(this.getAttribute("exclude-id")));
    }
    return Array.from(items.values(), (item) => `id:${item}`).slice(0, parseInt(this.getAttribute("products-count"))).join(" OR ");
  }
  async #loadProducts() {
    if (this.#isLoaded) {
      return;
    }
    this.#isLoaded = true;
    const section = this.closest(".shopify-section"), url = `${Shopify.routes.root}search?type=product&q=${this.#searchQueryString}&section_id=${extractSectionId(section)}`, response = await fetch(url, { priority: "low" });
    const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), recentlyViewedProductsElement = tempDiv.querySelector("recently-viewed-products");
    if (recentlyViewedProductsElement.childElementCount > 0) {
      this.replaceChildren(...document.importNode(recentlyViewedProductsElement, true).childNodes);
    } else {
      section.remove();
    }
  }
};
if (!window.customElements.get("recently-viewed-products")) {
  window.customElements.define("recently-viewed-products", RecentlyViewedProducts);
}
